import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import type { DigitalWorkerDetail, DiscordBot } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

const DISCORD_ICON = (
  <svg className="h-8 w-8 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1569 2.4189z" />
  </svg>
)

const DISCORD_ICON_MUTED = (
  <svg className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1569 2.4189z" />
  </svg>
)

interface Props {
  worker: DigitalWorkerDetail
  discordBots: DiscordBot[]
  canManage: boolean
}

export default function DiscordChannelCard({ worker, discordBots, canManage }: Props) {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedBot, setSelectedBot] = useState(discordBots[0]?.key ?? '')
  const [connecting, setConnecting] = useState(false)

  const isConnected = !!worker.discord_guild_id
  const isReady = worker.provisioning_status === 'ready'
  const canConnect = canManage && isReady && discordBots.length > 0

  const connectBot = async (botKey: string) => {
    setConnecting(true)
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const res = await fetch(`/dashboard/digital_workers/${worker.id}/connect_discord?bot_key=${encodeURIComponent(botKey)}`, {
        headers: { 'X-CSRF-Token': csrfToken },
      })
      if (!res.ok) {
        let message = 'Falha ao conectar Discord'
        try {
          const data = await res.json()
          if (typeof data.error === 'string' && data.error.length > 0) message = data.error
        } catch {
          // Keep the generic fallback when the server response is not JSON.
        }
        throw new Error(message)
      }
      const data = await res.json()
      if (data.url) {
        window.open(data.url, '_blank')
        setModalOpen(false)
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erro ao conectar Discord. Tente novamente.')
    } finally {
      setConnecting(false)
    }
  }

  if (isConnected) {
    const botName = worker.discord_bot_name || worker.discord_bot_key
    return (
      <div className="relative rounded-xl border border-success/40 bg-success/10 p-4">
        <div className="flex items-center gap-3">
          {DISCORD_ICON}
          <div>
            <p className="font-medium text-foreground">Discord</p>
            <p className="text-xs text-muted-foreground">{botName}</p>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-success/12 px-2 py-0.5 text-xs font-medium text-success">
            <span className="w-1.5 h-1.5 rounded-full bg-success" />
            Ativo
          </span>
        </div>
        {canManage && (
          <button
            type="button"
            className="mt-3 text-xs text-destructive transition-opacity duration-150 hover:opacity-80 cursor-pointer"
            onClick={() => {
              if (confirm('Desconectar o Discord deste colaborador?')) {
                router.delete(`/dashboard/digital_workers/${worker.id}/disconnect_discord`, { preserveScroll: true })
              }
            }}
          >
            Desconectar
          </button>
        )}
      </div>
    )
  }

  if (canConnect) {
    const handleClick = () => {
      if (discordBots.length === 1) connectBot(discordBots[0].key)
      else setModalOpen(true)
    }

    return (
      <>
        <button
          type="button"
          className="rounded-lg border border-border bg-card p-4 hover:border-[#5865F2] hover:bg-[#5865F2]/5 transition-colors cursor-pointer text-left w-full"
          onClick={handleClick}
        >
          <div className="flex items-center gap-3">
            {DISCORD_ICON}
            <div>
              <p className="font-medium text-foreground">Discord</p>
              <p className="text-xs text-[#5865F2]">Conectar</p>
            </div>
          </div>
        </button>
        <Dialog open={modalOpen} onOpenChange={(open) => !open && setModalOpen(false)}>
          <DialogContent className="max-w-[400px]">
            <DialogHeader><DialogTitle>Conectar Discord</DialogTitle></DialogHeader>
            <div className="grid gap-2">
              <Label>Selecione o bot</Label>
              <Select value={selectedBot} onValueChange={setSelectedBot}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {discordBots.map((bot) => <SelectItem key={bot.key} value={bot.key}>{bot.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button disabled={connecting} onClick={() => connectBot(selectedBot)}>
                {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
                Conectar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-muted/50 p-4 opacity-60 cursor-not-allowed">
      <div className="flex items-center gap-3">
        {DISCORD_ICON_MUTED}
        <div>
          <p className="font-medium text-foreground">Discord</p>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {!isReady ? 'Aguardando provisionamento' : 'Indisponível'}
          </span>
        </div>
      </div>
    </div>
  )
}
